/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Hook for managing audio recording in chat.
 *
 * Handles audio recording, playback, and cleanup.
 * Provides a clean interface for audio recording operations.
 *
 * @example
 * ```tsx
 * const { isRecording, startRecording, stopRecording, audioBlob, audioInputRef } = useAudioRecording();
 *
 * <button onClick={isRecording ? stopRecording : startRecording}>
 *   {isRecording ? 'Stop' : 'Record'}
 * </button>
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { logger } from '@utils/logger'

export interface UseAudioRecordingReturn {
  /** Whether recording is currently in progress */
  isRecording: boolean

  /** Start audio recording; check ``ok`` before treating recording as active. */
  startRecording: () => Promise<{ ok: true } | { ok: false; error: string }>

  /** Stop audio recording and get the blob */
  stopRecording: () => Promise<Blob | null>

  /** Cancel current recording without saving */
  cancelRecording: () => void

  /** The recorded audio blob (null if no recording) */
  audioBlob: Blob | null

  /** Ref for audio input element */
  audioInputRef: React.RefObject<HTMLInputElement>

  /** Open audio input picker */
  openAudioPicker: () => void

  /** Whether audio recording is supported */
  isSupported: boolean

  /** Error message if recording failed */
  error: string | null
}

/**
 * Hook for managing audio recording
 */
export const useAudioRecording = (): UseAudioRecordingReturn => {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSupported, setIsSupported] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const audioInputRef = useRef<HTMLInputElement>(null)

  // Check if MediaRecorder is supported
  useEffect(() => {
    setIsSupported(
      typeof navigator !== 'undefined' &&
        typeof navigator.mediaDevices !== 'undefined' &&
        typeof MediaRecorder !== 'undefined'
    )
  }, [])

  /**
   * Start audio recording
   */
  const startRecording = useCallback(async (): Promise<
    { ok: true } | { ok: false; error: string }
  > => {
    if (!isSupported) {
      const msg = 'Audio recording is not supported in this browser'
      setError(msg)
      return { ok: false, error: msg }
    }

    try {
      setError(null)

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        setAudioBlob(blob)

        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop())
      }

      // Handle errors
      mediaRecorder.onerror = (event) => {
        setError('Recording error occurred')
        logger.error('MediaRecorder error:', event)
      }

      // Start recording
      mediaRecorder.start()
      setIsRecording(true)
      return { ok: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording'
      setError(errorMessage)
      logger.error('Error starting recording:', err)
      return { ok: false, error: errorMessage }
    }
  }, [isSupported])

  /**
   * Stop audio recording
   */
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!mediaRecorderRef.current || !isRecording) {
      return null
    }

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current!

      // Set up one-time handler for when recording stops
      const handleStop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        setAudioBlob(blob)
        setIsRecording(false)
        mediaRecorderRef.current = null
        resolve(blob)
      }

      mediaRecorder.onstop = handleStop

      // Stop recording
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop()
      } else {
        // Already stopped, resolve immediately
        handleStop()
      }
    })
  }, [isRecording])

  /**
   * Cancel recording without saving
   */
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop())
      mediaRecorderRef.current = null
      audioChunksRef.current = []
      setIsRecording(false)
      setAudioBlob(null)
    }
  }, [isRecording])

  /**
   * Open audio input picker
   */
  const openAudioPicker = useCallback(() => {
    audioInputRef.current?.click()
  }, [])

  // Cleanup on unmount
  // CRITICAL: Cleanup function must not depend on state to avoid stale closures
  useEffect(() => {
    return () => {
      // Always cleanup media recorder on unmount, regardless of state
      if (mediaRecorderRef.current) {
        try {
          if (mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop()
          }
          // Stop all media tracks to release microphone/camera
          mediaRecorderRef.current.stream?.getTracks().forEach((track) => track.stop())
        } catch (err) {
          // Ignore errors during cleanup (component is unmounting anyway)
          logger.debug('Error during audio recording cleanup', err)
        }
      }
    }
  }, []) // Empty deps - cleanup runs only on unmount

  return {
    isRecording,
    startRecording,
    stopRecording,
    cancelRecording,
    audioBlob,
    audioInputRef,
    openAudioPicker,
    isSupported,
    error,
  }
}
